import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import {
  ResponseDtoPageServiceProviderOverViewDto,
  ResponseDtoServiceProviderMetadataDto,
  ResponseDtoServiceProviderOverViewDto,
  ServiceProviderAddressDto,
  ServiceProviderNameAliasDto,
  ServiceProviderOverViewDto,
} from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';

// Response type for service provider merge operation
// export interface MergeServiceProviderParams {
//   oldServiceProviderId: number;
//   newServiceProviderId: number;
// }

/**
 * Hook to fetch service providers from /api/service-providers/query (real API)
 * @param params Request body for pagination, sorting, and filtering
 */
export const useServiceProviderListApi = (params: any): UseQueryResult<ResponseDtoPageServiceProviderOverViewDto> =>
  useQuery(
    ['serviceProvidersApi', params],
    async () => {
      const res = await tFinancialService.api.getAllServiceProviders(params);
      return res.data;
    },
    { keepPreviousData: true }
  );

/**
 * Hook to fetch service provider metadata from /api/service-providers/metadata (real API)
 * @param params Request body for metadata filtering
 */
export const useServiceProviderMetadataApi = (params: any): UseQueryResult<ResponseDtoServiceProviderMetadataDto> =>
  useQuery(['serviceProviderMetadataApi', params], async () => {
    // remove paginationRequest from params before sending to API
    const { paginationRequest, ...rest } = params;
    const res = await tFinancialService.api.getAllServiceProvidersMetaData(rest);
    return res.data;
  });

/**
 * Hook to fetch service provider details, aliases, and addresses using /api/service-providers/search
 * @param id Service provider ID
 */
export const useServiceProviderDetailApi = (id?: number, code?: string): UseQueryResult<ResponseDtoServiceProviderOverViewDto> =>
  useQuery(
    ['serviceProviderDetailApi', id],
    async () => {
      if (!id) return null;
      const res = await tFinancialService.api.getServiceProviderByIdOrCode({ id, code });
      return res.data;
    },
    { enabled: !!id }
  );

/**
 * Hook to create or update a service provider
 * @returns Mutation function for upserting service provider
 */
export const useUpsertServiceProvider = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (serviceProvider: ServiceProviderOverViewDto) => {
      // Use real API for upsert
      const { updatedOn, ...rest } = serviceProvider;
      const res = await tFinancialService.api.upsertServiceProviders([rest]);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['serviceProvidersApi']);
      },
    }
  );
};

/**
 * Hook to delete a service provider
 * @returns Mutation function for deleting a service provider
 */
export const useDeleteServiceProvider = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (id: number) => {
      // Use real API for delete
      const res = await tFinancialService.api.deleteServiceProvider(id);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['serviceProvidersApi']);
      },
    }
  );
};

/**
 * Hook to create or update a service provider alias
 * @returns Mutation function for upserting a service provider alias
 */
export const useUpsertServiceProviderAlias = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ serviceProviderId, alias }: { serviceProviderId: number; alias: Partial<ServiceProviderNameAliasDto> }) => {
      // Use real API for upsert
      const reqBody = { ...alias, serviceProviderId };
      const res = await tFinancialService.api.upsertServiceProviderAlias(reqBody);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['serviceProviderDetailApi']);
      },
    }
  );
};

/**
 * Hook to delete a service provider alias
 * @returns Mutation function for deleting a service provider alias
 */
export const useDeleteServiceProviderAlias = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ aliasId }: { aliasId: number }) => {
      // Use real API for delete
      const res = await tFinancialService.api.deleteServiceProviderAlias(aliasId);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['serviceProviderDetailApi']);
      },
    }
  );
};

/**
 * Hook to create or update a service provider address
 * @returns Mutation function for upserting a service provider address
 */
export const useUpsertServiceProviderAddress = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ serviceProviderId, address }: { serviceProviderId: number; address: ServiceProviderAddressDto }) => {
      // Use real API for upsert
      const reqBody = { ...address, serviceProviderId };
      const res = await tFinancialService.api.upsertServiceProviderAddress(reqBody);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['serviceProviderDetailApi']);
      },
    }
  );
};

/**
 * Hook to delete a service provider address
 * @returns Mutation function for deleting a service provider address
 */
export const useDeleteServiceProviderAddress = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ addressId }: { addressId: number }) => {
      // Use real API for delete (single address)
      const res = await tFinancialService.api.deleteServiceProviderAddresses([addressId]);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['serviceProviderDetailApi']);
      },
    }
  );
};

/**
 * Fetches all service providers for use in dropdown selectors.
 * Loads once with page size 1000 and caches for 10 minutes.
 * MUI Autocomplete handles client-side filtering — no server-side search needed.
 */
export const useAllServiceProvidersDropdown = (): UseQueryResult<ResponseDtoPageServiceProviderOverViewDto> =>
  useQuery(
    ['serviceProvidersDropdown'],
    async () => {
      const res = await tFinancialService.api.getAllServiceProviders({
        paginationRequest: { page: 0, size: 1000 },
      });
      return res.data;
    },
    {
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

/**
 * Hook to merge one service provider into another
 * @returns Mutation function for merging service providers
 */
// export const useMergeServiceProvider = () => {
//   const queryClient = useQueryClient();

//   return useMutation(
//     async ({ oldServiceProviderId, newServiceProviderId }: MergeServiceProviderParams) => {
//       // In a real app, this would be an API call
//       console.log(`Merging service provider ${oldServiceProviderId} into ${newServiceProviderId}`);

//       // Simulate API call
//       await new Promise((resolve) => setTimeout(resolve, 800));

//       // Update dummy data to simulate the merge
//       // In a real app, this would be handled by the backend

//       return {
//         data: true,
//         message: 'Service provider merged successfully',
//         status: 'SUCCESS',
//       };
//     },
//     {
//       onSuccess: () => {
//         // Invalidate and refetch all service provider related queries
//         queryClient.invalidateQueries(['serviceProviders']);
//         queryClient.invalidateQueries(['serviceProvider']);
//         queryClient.invalidateQueries(['serviceProviderAliases']);
//         queryClient.invalidateQueries(['serviceProviderAddresses']);
//       },
//     }
//   );
// };

/**
 * Hook to upsert (create/update) service provider(s)
 */
export const useUpsertServiceProviderApi = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (providers: any[]) => {
      const res = await tFinancialService.api.upsertServiceProviders(providers);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['serviceProvidersApi']);
      },
    }
  );
};

/**
 * Hook to delete a service provider
 */
export const useDeleteServiceProviderApi = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (id: number) => {
      const res = await tFinancialService.api.deleteServiceProvider(id);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['serviceProvidersApi']);
      },
    }
  );
};

/**
 * Hook to upsert (create/update) service provider alias
 */
export const useUpsertServiceProviderAliasApi = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (alias: any) => {
      const res = await tFinancialService.api.upsertServiceProviderAlias(alias);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['serviceProviderDetailApi']);
      },
    }
  );
};

/**
 * Hook to upsert (create/update) service provider address
 */
export const useUpsertServiceProviderAddressApi = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (address: any) => {
      const res = await tFinancialService.api.upsertServiceProviderAddress(address);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['serviceProviderDetailApi']);
      },
    }
  );
};
